/**
 * DriverProfileSettingsPage — carrier admin: edit single driver profile + equipment
 * /settings/drivers/:driverId?carrierId=<orgId>
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { settingsApi, api, type DriverProfile } from '../lib/api'
import { useAuth } from '../lib/auth-context'

const CDL_CLASSES   = ['A', 'B', 'C']
const ENDORSEMENTS  = ['H', 'N', 'P', 'T', 'X', 'S', 'L']
const RESTRICTIONS  = ['B', 'E', 'K', 'L', 'M', 'N', 'O', 'V', 'W']

function TagSelector({ label, options, value, onChange }: {
  label: string
  options: string[]
  value: string[]
  onChange: (v: string[]) => void
}) {
  const toggle = (tag: string) => {
    onChange(value.includes(tag) ? value.filter(t => t !== tag) : [...value, tag])
  }
  return (
    <div>
      <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
      <div className="flex gap-1 flex-wrap">
        {options.map(tag => (
          <button key={tag} type="button" onClick={() => toggle(tag)}
            className={`px-2.5 py-1 rounded text-xs font-semibold border transition-colors
              ${value.includes(tag)
                ? 'bg-blue-700 text-white border-blue-700'
                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
            {tag}
          </button>
        ))}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  )
}

const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

export default function DriverProfileSettingsPage() {
  const { driverId } = useParams<{ driverId: string }>()
  const [params] = useSearchParams()
  const { user, hasRole } = useAuth()
  const qc = useQueryClient()

  const carrierId = params.get('carrierId') || user?.orgId || ''
  const canEdit   = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN')

  // ── Load current state ────────────────────────────────────────────────────
  const driverQ = useQuery({
    queryKey: ['driver-profile', carrierId, driverId],
    queryFn: () => settingsApi.getDriverProfile(carrierId, driverId!).then(r => r.data),
    enabled: !!carrierId && !!driverId,
  })

  const equipsQ = useQuery({
    queryKey: ['equipment-combos-list'],
    queryFn: () => api.equipment.combos().then(r => r.data?.combos ?? []),
  })

  // ── Profile form state ─────────────────────────────────────────────────────
  const [cdlClass, setCdlClass] = useState('')
  const [licNum, setLicNum] = useState('')
  const [licState, setLicState] = useState('')
  const [licExp, setLicExp] = useState('')
  const [medExp, setMedExp] = useState('')
  const [endorsements, setEndorsements] = useState<string[]>([])
  const [restrictions, setRestrictions] = useState<string[]>([])
  const [yearsExp, setYearsExp] = useState('')
  const [phone, setPhone] = useState('')
  const [ecName, setEcName] = useState('')
  const [ecPhone, setEcPhone] = useState('')
  const [unitLabel, setUnitLabel] = useState('')
  const [notes, setNotes] = useState('')

  // ── Equipment form state ───────────────────────────────────────────────────
  const [comboKey, setComboKey] = useState('')
  const [equipUnitLabel, setEquipUnitLabel] = useState('')

  // ── Messages ───────────────────────────────────────────────────────────────
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [equipMsg,   setEquipMsg]   = useState<{ ok: boolean; text: string } | null>(null)

  // Sync form state when data loads
  useEffect(() => {
    if (!driverQ.data) return
    const p = driverQ.data.profile
    if (p) {
      setCdlClass(p.cdlClass ?? '')
      setLicNum(p.licenseNumber ?? '')
      setLicState(p.licenseState ?? '')
      setLicExp(p.licenseExpires ?? '')
      setMedExp(p.medicalCertExpires ?? '')
      setEndorsements(p.endorsements ?? [])
      setRestrictions(p.restrictions ?? [])
      setYearsExp(p.yearsExperience != null ? String(p.yearsExperience) : '')
      setPhone(p.phone ?? '')
      setEcName(p.emergencyContactName ?? '')
      setEcPhone(p.emergencyContactPhone ?? '')
      setUnitLabel(p.unitLabel ?? '')
      setNotes(p.notes ?? '')
    }
    const eq = driverQ.data.equipment
    if (eq) {
      setComboKey(eq.comboKey ?? '')
      setEquipUnitLabel(eq.unitLabel ?? '')
    }
  }, [driverQ.data])

  // ── Save profile ───────────────────────────────────────────────────────────
  const profileMut = useMutation({
    mutationFn: async () => {
      const data: Partial<DriverProfile> = {
        cdlClass:             cdlClass || null,
        licenseNumber:        licNum || null,
        licenseState:         licState || null,
        licenseExpires:       licExp || null,
        medicalCertExpires:   medExp || null,
        endorsements,
        restrictions,
        yearsExperience:      yearsExp ? Number(yearsExp) : null,
        phone:                phone || null,
        emergencyContactName: ecName || null,
        emergencyContactPhone:ecPhone || null,
        unitLabel:            unitLabel || null,
        notes:                notes || null,
      }
      // Upsert (POST creates or updates)
      return settingsApi.upsertDriverProfile(carrierId, { auxUserId: driverId!, ...data })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['driver-profile', carrierId, driverId] })
      qc.invalidateQueries({ queryKey: ['driver-profiles', carrierId] })
      setProfileMsg({ ok: true, text: 'Profile saved.' })
      setTimeout(() => setProfileMsg(null), 3000)
    },
    onError: (err: any) => setProfileMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  // ── Save equipment assignment ──────────────────────────────────────────────
  const equipMut = useMutation({
    mutationFn: () =>
      settingsApi.assignEquipment(carrierId, driverId!, {
        comboKey: comboKey || undefined,
        unitLabel: equipUnitLabel || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['driver-profile', carrierId, driverId] })
      qc.invalidateQueries({ queryKey: ['driver-profiles', carrierId] })
      setEquipMsg({ ok: true, text: 'Equipment assigned. Driver\'s jobs will auto-load this combo.' })
      setTimeout(() => setEquipMsg(null), 5000)
    },
    onError: (err: any) => setEquipMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  const removeEquipMut = useMutation({
    mutationFn: () => settingsApi.removeEquipment(carrierId, driverId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['driver-profile', carrierId, driverId] })
      qc.invalidateQueries({ queryKey: ['driver-profiles', carrierId] })
      setComboKey('')
      setEquipUnitLabel('')
      setEquipMsg({ ok: true, text: 'Equipment assignment removed.' })
      setTimeout(() => setEquipMsg(null), 3000)
    },
    onError: (err: any) => setEquipMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  if (driverQ.isLoading) {
    return <div className="p-6 text-gray-400 text-sm">Loading driver profile…</div>
  }
  if (driverQ.error) {
    return <div className="p-6 text-red-600 text-sm">Failed to load driver. {(driverQ.error as any)?.message}</div>
  }

  const entry = driverQ.data
  const warnings = entry?.profile?.complianceWarnings ?? []

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{entry?.name ?? 'Driver Profile'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{entry?.email}</p>
          {warnings.length > 0 && (
            <div className="mt-2 space-y-1">
              {warnings.map((w, i) => (
                <div key={i} className={`text-xs font-medium ${w.includes('EXPIRED') ? 'text-red-700' : 'text-amber-700'}`}>
                  ⚠ {w}
                </div>
              ))}
            </div>
          )}
        </div>
        <Link to={`/settings/drivers?carrierId=${carrierId}`}
          className="text-sm text-blue-600 hover:text-blue-800">← Roster</Link>
      </div>

      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 text-sm">
          View only — you do not have permission to edit driver profiles.
        </div>
      )}

      {/* ── CDL + License ───────────────────────────────────────────────────── */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">License &amp; Compliance</h2>
        {profileMsg && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${
            profileMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'
          }`}>{profileMsg.text}</div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="CDL Class">
            <select value={cdlClass} onChange={e => setCdlClass(e.target.value)}
              disabled={!canEdit} className={inputCls}>
              <option value="">Not set</option>
              {CDL_CLASSES.map(c => <option key={c} value={c}>CDL-{c}</option>)}
            </select>
          </Field>
          <Field label="License State (2-letter)">
            <input value={licState} onChange={e => setLicState(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="TX" maxLength={2} disabled={!canEdit} className={inputCls} />
          </Field>
          <Field label="License Number">
            <input value={licNum} onChange={e => setLicNum(e.target.value)}
              placeholder="DL12345678" disabled={!canEdit} className={inputCls} />
          </Field>
          <Field label="License Expires">
            <input type="date" value={licExp} onChange={e => setLicExp(e.target.value)}
              disabled={!canEdit} className={inputCls} />
          </Field>
          <Field label="Medical Cert Expires">
            <input type="date" value={medExp} onChange={e => setMedExp(e.target.value)}
              disabled={!canEdit} className={inputCls} />
          </Field>
          <Field label="Years Experience">
            <input type="number" value={yearsExp} onChange={e => setYearsExp(e.target.value)}
              min={0} max={60} placeholder="0" disabled={!canEdit} className={inputCls} />
          </Field>
        </div>
        <div className="mt-4 space-y-3">
          <TagSelector label="Endorsements" options={ENDORSEMENTS}
            value={endorsements} onChange={canEdit ? setEndorsements : () => {}} />
          <TagSelector label="Restrictions" options={RESTRICTIONS}
            value={restrictions} onChange={canEdit ? setRestrictions : () => {}} />
        </div>
      </section>

      {/* ── Contact + Admin ─────────────────────────────────────────────────── */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact &amp; Admin</h2>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone">
            <input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+1 555-0100" disabled={!canEdit} className={inputCls} />
          </Field>
          <Field label="Unit Label">
            <input value={unitLabel} onChange={e => setUnitLabel(e.target.value)}
              placeholder="Unit 12" disabled={!canEdit} className={inputCls} />
          </Field>
          <Field label="Emergency Contact Name">
            <input value={ecName} onChange={e => setEcName(e.target.value)}
              placeholder="Jane Smith" disabled={!canEdit} className={inputCls} />
          </Field>
          <Field label="Emergency Contact Phone">
            <input value={ecPhone} onChange={e => setEcPhone(e.target.value)}
              placeholder="+1 555-0101" disabled={!canEdit} className={inputCls} />
          </Field>
          <div className="col-span-2">
            <Field label="Notes (internal)">
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                rows={2} placeholder="Internal notes for dispatchers…"
                disabled={!canEdit}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </Field>
          </div>
        </div>
        {canEdit && (
          <div className="mt-4">
            <button onClick={() => profileMut.mutate()} disabled={profileMut.isPending}
              className="bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-50">
              {profileMut.isPending ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
        )}
      </section>

      {/* ── Equipment Assignment ─────────────────────────────────────────────── */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Equipment Assignment</h2>
        <p className="text-sm text-gray-500 mb-4">
          When a combo is assigned, this driver's trailer planner will auto-load it for new jobs.
        </p>
        {equipMsg && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${
            equipMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'
          }`}>{equipMsg.text}</div>
        )}

        {/* Current assignment summary */}
        {entry?.equipment && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <div className="font-semibold text-blue-900 mb-0.5">Currently assigned:</div>
            {entry.equipment.unitLabel && <div className="text-blue-800">{entry.equipment.unitLabel}</div>}
            <div className="text-blue-700">
              {[
                entry.equipment.tractorManufacturer, entry.equipment.tractorModel,
              ].filter(Boolean).join(' ')} + {[
                entry.equipment.trailerManufacturer, entry.equipment.trailerModel,
              ].filter(Boolean).join(' ')}
            </div>
            {entry.equipment.comboKey && (
              <div className="text-xs text-blue-500 font-mono mt-1">{entry.equipment.comboKey}</div>
            )}
          </div>
        )}

        {canEdit && (
          <div className="space-y-3">
            <Field label="Equipment Combo">
              {equipsQ.isLoading
                ? <div className="text-xs text-gray-400">Loading combos…</div>
                : (
                  <select value={comboKey} onChange={e => setComboKey(e.target.value)} className={inputCls}>
                    <option value="">— Select combo —</option>
                    {(equipsQ.data ?? []).map((c: any) => (
                      <option key={c.comboKey ?? c.id} value={c.comboKey ?? c.id}>
                        {c.name ?? `${c.tractorSpec?.manufacturer ?? ''} + ${c.trailerSpec?.manufacturer ?? ''}`}
                        {c.comboKey ? ` (${c.comboKey})` : ''}
                      </option>
                    ))}
                  </select>
                )}
            </Field>
            <Field label='Unit Label (e.g. "Tractor 12 / Trailer 7")'>
              <input value={equipUnitLabel} onChange={e => setEquipUnitLabel(e.target.value)}
                placeholder="Tractor 12 / Trailer 7" className={inputCls} />
            </Field>
            <div className="flex gap-3 pt-1">
              <button onClick={() => equipMut.mutate()}
                disabled={equipMut.isPending || !comboKey}
                className="bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-50">
                {equipMut.isPending ? 'Assigning…' : 'Assign Equipment'}
              </button>
              {entry?.equipment && (
                <button onClick={() => removeEquipMut.mutate()}
                  disabled={removeEquipMut.isPending}
                  className="text-sm text-red-600 hover:text-red-800 font-medium px-4 py-2.5 rounded-lg border border-red-200 hover:border-red-400 disabled:opacity-50">
                  {removeEquipMut.isPending ? 'Removing…' : 'Remove Assignment'}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
